import React, { useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";

import apiClient from "../../../services/apiClient";
import { ASSIGNMENT_PLAN_REQUIRED_MESSAGE, canCreateAssignment } from "../../../utils/planUtils";
import MathBankAssignModal from "./MathBankAssignModal";
import MathBankDocPanel from "./MathBankDocPanel";
import MathBankRangePanel from "./MathBankRangePanel";
import { MathBankProblemCard } from "./MathBankControls";
import { DEFAULT_PROBLEM_FILTER, MAX_TOTAL } from "./mathBankShared";
import "./MathBankPage.css";

// 수학 계정의 첫 화면 '출제하기'(2026-11-04 대표: "수학 메인페이지에 젤 먼저 문항들 유형고르는거 띄워주고 그거출제하게하고").
// 들어가는 길이 둘이고 위의 탭으로 고른다. 주소의 ?tab=pdf 로 남아 새로 고쳐도 그대로다.
//   범위 골라 출제  과목 → 단원 → 소분류 → 세부유형을 고르고 조건으로 뽑는다(MathBankRangePanel)
//   내 PDF 출제     수학 사이트에 올린 문서를 폴더째 보고 문항을 체크해 담는다(MathBankDocPanel)
// 두 탭 모두 쌍둥이 · 손필기 해설 · 내 문항/문제은행으로 거른다(수학 사이트 7절). 담은 문제는 두 탭이 같이 쓰고,
// '출제하기' 를 누르면 제목 · 복습 · 마감일 · 학생만 고르는 창(MathBankAssignModal)이 뜬다. 출제 길은 서버 math_bank.py 하나다.
const ENTRY_TAB_LIST = [
  { value: "range", label: "범위 골라 출제" },
  { value: "pdf", label: "내 PDF 출제" },
];

const MathBankPage = () => {
  const { user } = useSelector((state) => state.auth);
  const canAssign = canCreateAssignment(user.plan);
  const [searchParams, setSearchParams] = useSearchParams();
  const entryTab = searchParams.get("tab") === "pdf" ? "pdf" : "range";

  const [filter, setFilter] = useState(DEFAULT_PROBLEM_FILTER);
  const [problemList, setProblemList] = useState([]);
  const [swappingId, setSwappingId] = useState("");
  const [isAssignOpen, setIsAssignOpen] = useState(false);
  const [defaultTitle, setDefaultTitle] = useState("");
  const pickedSectionRef = useRef(null);

  const selectEntryTab = (value) => setSearchParams(value === "pdf" ? { tab: "pdf" } : {}, { replace: true });

  const scrollToPicked = () =>
    requestAnimationFrame(() => pickedSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }));

  // 조건으로 뽑으면 담은 문제를 새로 뽑은 것으로 바꾼다
  const replacePicked = (pickedList, title) => {
    setProblemList(pickedList);
    setDefaultTitle(title);
    scrollToPicked();
  };

  // 문서에서 체크한 문제는 담은 문제 뒤에 붙인다(이미 담긴 것은 건너뛴다). 담았으면 true
  const addPicked = (pickedList, title) => {
    const existingIdSet = new Set(problemList.map((one) => one.problem_id));
    const nextList = [...problemList, ...pickedList.filter((one) => !existingIdSet.has(one.problem_id))];
    if (nextList.length > MAX_TOTAL) {
      alert(`한 번에 ${MAX_TOTAL}문제까지 낼 수 있어요.`);
      return false;
    }
    setProblemList(nextList);
    if (!defaultTitle) setDefaultTitle(title);
    return true;
  };

  const swap = async (problemId) => {
    if (swappingId) return;
    setSwappingId(problemId);
    try {
      const response = await apiClient.post(
        "/api/v1/assignment/math/bank/swap",
        { problem_id: problemId, screen_problem_ids: problemList.map((one) => one.problem_id), dbs: filter.dbList },
        { withAuth: true }
      );
      if (!response.data.problem) return alert("닮은 문제가 더 없습니다.");
      setProblemList((list) => list.map((one) => (one.problem_id === problemId ? response.data.problem : one)));
    } catch (error) {
      alert(error?.response?.data?.error || "문제를 바꾸지 못했습니다.");
    } finally {
      setSwappingId("");
    }
  };

  const clearPicked = () => {
    setProblemList([]);
    setDefaultTitle("");
  };

  const openAssign = () => {
    if (!problemList.length) return alert("문제를 담아주세요.");
    if (!canAssign) return alert(ASSIGNMENT_PLAN_REQUIRED_MESSAGE);
    setIsAssignOpen(true);
  };

  return (
    <div className="math-bank-page">
      <div className="math-bank-page__header">
        <h3 className="math-bank-page__title">출제하기</h3>
        <div className="math-bank-page__entry-tab-list" role="tablist">
          {ENTRY_TAB_LIST.map((one) => (
            <button
              key={one.value}
              type="button"
              role="tab"
              aria-selected={entryTab === one.value}
              className={`math-bank-page__entry-tab${entryTab === one.value ? " math-bank-page__entry-tab--active" : ""}`}
              onClick={() => selectEntryTab(one.value)}
            >
              {one.label}
            </button>
          ))}
        </div>
      </div>

      {/* 두 탭을 모두 그려 두고 하나만 보인다. 탭을 오가도 고른 범위 · 열어 둔 문서가 남는다 */}
      <div hidden={entryTab !== "range"}>
        <MathBankRangePanel
          filter={filter}
          onFilterChange={setFilter}
          hasPickedProblems={problemList.length > 0}
          onPicked={replacePicked}
        />
      </div>
      <div hidden={entryTab !== "pdf"}>
        <MathBankDocPanel
          isActive={entryTab === "pdf"}
          filter={filter}
          onFilterChange={setFilter}
          pickedProblemIdList={problemList.map((one) => one.problem_id)}
          onAddProblems={addPicked}
          onPicked={replacePicked}
        />
      </div>

      {problemList.length > 0 && (
        <section ref={pickedSectionRef} className="math-bank-page__card math-bank-page__picked">
          <div className="math-bank-page__card-head">
            <h4>담은 문제 {problemList.length}</h4>
            <div className="math-bank-page__head-actions">
              <button type="button" className="math-bank-page__secondary" onClick={clearPicked}>
                비우기
              </button>
            </div>
          </div>
          <ol className="math-bank-page__problem-list math-bank-page__problem-list--grid">
            {problemList.map((problem, index) => (
              <MathBankProblemCard
                key={problem.problem_id}
                problem={problem}
                index={index}
                actionList={
                  <>
                    <button type="button" className="math-bank-page__small-button" onClick={() => swap(problem.problem_id)}>
                      {swappingId === problem.problem_id ? "바꾸는 중" : "바꾸기"}
                    </button>
                    <button
                      type="button"
                      className="math-bank-page__small-button"
                      onClick={() => setProblemList((list) => list.filter((one) => one.problem_id !== problem.problem_id))}
                    >
                      빼기
                    </button>
                  </>
                }
              />
            ))}
          </ol>
        </section>
      )}

      {problemList.length > 0 && (
        <div className="math-bank-page__dock">
          <button type="button" className="math-bank-page__dock-count" onClick={scrollToPicked}>
            담은 문제 <strong>{problemList.length}</strong>
          </button>
          <button type="button" className="math-bank-page__primary" onClick={openAssign}>
            출제하기
          </button>
        </div>
      )}

      {isAssignOpen && (
        <MathBankAssignModal
          problemList={problemList}
          defaultTitle={defaultTitle}
          onClose={() => setIsAssignOpen(false)}
          onAssigned={() => {
            setIsAssignOpen(false);
            clearPicked();
          }}
        />
      )}
    </div>
  );
};

export default MathBankPage;
